import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, fromEvent, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CrmAuthSessionService } from './crm-auth-session.service';
import { AuthService } from '../../../../../../../libs/core';

/**
 * Detects user inactivity (idle time).
 * After a configurable timeout with no user interaction, triggers logout.
 * Console-logs idle time updates.
 */
@Injectable({ providedIn: 'root' })
export class CrmIdleTimeoutService implements OnDestroy {
  /**
   * Idle timeout in milliseconds (default: 15 minutes).
   */
  private readonly idleTimeoutMs = 15 * 60 * 1000;
  private readonly checkIntervalMs = 10_000; // log every 10s

  private readonly authService = inject(AuthService);
  private readonly sessionService = inject(CrmAuthSessionService);
  private readonly router = inject(Router);

  private activitySub: Subscription | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private lastActivity = Date.now();
  private isRunning = false;

  /** The DOM events that reset the idle timer. */
  private readonly activityEvents = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
  ];

  start(): void {
    if (this.isRunning || typeof window === 'undefined') return;
    this.isRunning = true;
    this.lastActivity = Date.now();

    // Listen for user activity and reset the timer
    const streams = this.activityEvents.map((eventName) =>
      fromEvent(document, eventName, { passive: true }),
    );

    this.activitySub = merge(...streams)
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.lastActivity = Date.now();
      });

    // Periodic idle check
    this.intervalId = setInterval(() => this.checkIdle(), this.checkIntervalMs);

    // console.log(
    //   `[IdleTimeout] Started — idle timeout set to ${this.idleTimeoutMs / 1000}s (${this.idleTimeoutMs / 60_000}min).`,
    // );
  }

  stop(): void {
    this.activitySub?.unsubscribe();
    this.activitySub = null;

    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.isRunning = false;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private checkIdle(): void {
    if (!this.authService.isAuthenticated()) return;

    const now = Date.now();
    const idleMs = now - this.lastActivity;
    const idleSec = Math.round(idleMs / 1000);

    // console.log(
    //   `[IdleTimeout] Idle: ${idleSec}s / ${this.idleTimeoutMs / 1000}s timeout`,
    // );

    if (idleMs >= this.idleTimeoutMs) {
      // console.log(
      //   `[IdleTimeout] Idle timeout exceeded (${idleSec}s) — triggering logout.`,
      // );
      this.stop();
      this.sessionService.logout();
    }
  }
}